import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { siteConfig } from '../config/siteConfig';

// Safe version of SmartMatchingWizard that avoids useSearchParams initially
function SafeSmartMatchingWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    zipCode: '',
    serviceType: '',
    urgency: '',
    propertyType: '',
    damageSize: '',
    contactInfo: {
      name: '',
      phone: '',
      email: ''
    },
    privacyConsent: false,
    smsConsent: false  // Separate SMS opt-in per Twilio A2P 10DLC compliance
  });

  // Get ZIP from URL manually to avoid useSearchParams
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const zipFromUrl = urlParams.get('zip');
    if (zipFromUrl && zipFromUrl.length === 5) {
      setFormData(prev => ({ ...prev, zipCode: zipFromUrl }));
    }
  }, []);

  const updateFormData = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const nextStep = () => {
    if (currentStep < 5) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    // Save contact info to sessionStorage for persistence across routes (A2P compliance)
    try {
      sessionStorage.setItem('ffp_contact_info', JSON.stringify({
        name: formData.contactInfo.name,
        phone: formData.contactInfo.phone,
        email: formData.contactInfo.email,
        termsConsent: formData.privacyConsent,
        smsConsent: formData.smsConsent,
        savedAt: new Date().toISOString()
      }));
    } catch (storageErr) {
      console.warn('Unable to save contact info to session storage:', storageErr);
    }

    try {
      // Navigate to results page
      navigate(`/results/${formData.zipCode}`, {
        state: {
          wizardData: formData,
          fromWizard: true
        }
      });
    } catch (error) {
      navigate(`/results/${formData.zipCode}`, {
        state: {
          wizardData: formData,
          fromWizard: true
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const progressPercent = Math.round((currentStep / 5) * 100);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="ffp-container-reading max-w-xl mx-auto px-4">
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-medium text-gray-700">Step {currentStep} of 5</span>
            <span className="text-xs text-gray-500">{progressPercent}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-orange-500 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
        </div>

        {/* Step Content */}
        <div className="ffp-wizard-step">
          {currentStep === 1 && (
            <div className="text-center">
              <h2 className="text-xl font-bold mb-5 text-gray-900">What type of damage do you have?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { value: 'water', label: 'Water Damage', icon: '💧', desc: 'Flooding, leaks, burst pipes' },
                  { value: 'fire', label: 'Fire Damage', icon: '🔥', desc: 'Smoke, soot, fire restoration' },
                  { value: 'mold', label: 'Mold Issues', icon: '🦠', desc: 'Mold removal & remediation' },
                  { value: 'storm', label: 'Storm Damage', icon: '⛈️', desc: 'Wind, hail, tree damage' }
                ].map((service) => (
                  <button
                    key={service.value}
                    onClick={() => {
                      updateFormData('serviceType', service.value);
                      nextStep();
                    }}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 text-left hover:border-orange-500 hover:shadow-lg ${
                      formData.serviceType === service.value
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="text-2xl mb-1.5">{service.icon}</div>
                    <div className="font-bold text-base mb-1">{service.label}</div>
                    <div className="text-gray-600 text-xs">{service.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="text-center">
              <h2 className="text-xl font-bold mb-5 text-gray-900">How urgent is this situation?</h2>
              <div className="space-y-3">
                {[
                  { value: 'emergency', label: 'Emergency - Need help now!', desc: 'Active flooding, fire, immediate danger', color: 'red' },
                  { value: 'urgent', label: 'Urgent - Within 24 hours', desc: 'Significant damage, needs quick response', color: 'orange' },
                  { value: 'soon', label: 'Soon - Within a few days', desc: 'Moderate damage, can wait briefly', color: 'yellow' },
                  { value: 'planning', label: 'Planning - Gathering information', desc: 'No immediate rush, evaluating service needs', color: 'green' }
                ].map((urgency) => (
                  <button
                    key={urgency.value}
                    onClick={() => {
                      updateFormData('urgency', urgency.value);
                      nextStep();
                    }}
                    className={`w-full p-3 rounded-lg border-2 transition-all duration-200 text-left hover:shadow-lg ${
                      formData.urgency === urgency.value
                        ? `border-${urgency.color}-500 bg-${urgency.color}-50`
                        : 'border-gray-200 bg-white hover:border-orange-500'
                    }`}
                  >
                    <div className="font-bold text-base mb-1">{urgency.label}</div>
                    <div className="text-gray-600 text-xs">{urgency.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="text-center">
              <h2 className="text-xl font-bold mb-5 text-gray-900">What's your ZIP code?</h2>
              <p className="text-gray-600 mb-5 text-sm">We'll find restoration professionals in your area</p>
              <div className="max-w-md mx-auto">
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => updateFormData('zipCode', e.target.value)}
                  placeholder="Enter ZIP code"
                  className="w-full p-3 border-2 border-gray-300 rounded-lg text-center text-xl focus:border-orange-500 focus:outline-none"
                  maxLength={5}
                />
                <button
                  onClick={nextStep}
                  disabled={formData.zipCode.length !== 5}
                  className="w-full mt-4 p-3 bg-orange-500 text-white font-bold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-600 transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="text-center">
              <h2 className="text-xl font-bold mb-5 text-gray-900">Property details</h2>
              <div className="space-y-4">
                <div>
                  <label htmlFor="propertyType" className="block text-left font-medium mb-2">Property Type</label>
                  <select
                    id="propertyType"
                    value={formData.propertyType}
                    onChange={(e) => updateFormData('propertyType', e.target.value)}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
                    aria-required="true"
                  >
                    <option value="">Select property type</option>
                    <option value="residential">Residential Home</option>
                    <option value="condo">Condo/Townhouse</option>
                    <option value="commercial">Commercial Building</option>
                    <option value="rental">Rental Property</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="damageSize" className="block text-left font-medium mb-2">Damage Size</label>
                  <select
                    id="damageSize"
                    value={formData.damageSize}
                    onChange={(e) => updateFormData('damageSize', e.target.value)}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
                    aria-required="true"
                  >
                    <option value="">Select damage size</option>
                    <option value="small">Small (1 room)</option>
                    <option value="medium">Medium (2-3 rooms)</option>
                    <option value="large">Large (Multiple rooms/floors)</option>
                    <option value="whole">Whole building</option>
                  </select>
                </div>
                <button
                  onClick={nextStep}
                  disabled={!formData.propertyType || !formData.damageSize}
                  className="w-full mt-4 p-3 bg-orange-500 text-white font-bold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-600 transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <div className="text-center">
              <h2 className="text-xl font-bold mb-5 text-gray-900">Contact Information</h2>
              <div className="space-y-3">
                <input
                  type="text"
                  value={formData.contactInfo.name}
                  onChange={(e) => updateFormData('contactInfo.name', e.target.value)}
                  placeholder="Your name"
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
                />
                <input
                  type="tel"
                  value={formData.contactInfo.phone}
                  onChange={(e) => updateFormData('contactInfo.phone', e.target.value)}
                  placeholder="Phone number"
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
                />
                <input
                  type="email"
                  value={formData.contactInfo.email}
                  onChange={(e) => updateFormData('contactInfo.email', e.target.value)}
                  placeholder="Email address (optional)"
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
                />

                {/* SECTION 1: Privacy & Terms Consent (REQUIRED) */}
                <div className="border-2 border-gray-300 rounded-lg p-4 text-left bg-gray-50">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="privacyConsent"
                      checked={formData.privacyConsent}
                      onChange={(e) => updateFormData('privacyConsent', e.target.checked)}
                      className="mt-1 mr-3 h-5 w-5 text-orange-600 focus:ring-orange-500 border-2 border-gray-400 rounded"
                    />
                    <label htmlFor="privacyConsent" className="cursor-pointer flex-grow">
                      <div className="flex items-center mb-2">
                        <span className="mr-2 text-lg">🔒</span>
                        <span className="font-bold text-gray-900 text-base">Privacy & Terms Agreement</span>
                        <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Required</span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>I agree to the <a href="/privacy" target="_blank" className="text-orange-600 hover:underline">Privacy Policy</a> and <a href="/terms" target="_blank" className="text-orange-600 hover:underline">Terms of Service</a>.</p>
                        <p className="text-xs text-gray-500">Your information is used only to match you with restoration professionals. We never sell your data.</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* SECTION 2: SMS Opt-In (SEPARATE - Twilio A2P 10DLC Compliance) */}
                <div className="border-2 border-blue-200 rounded-lg p-4 text-left bg-blue-50">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="smsConsent"
                      checked={formData.smsConsent}
                      onChange={(e) => updateFormData('smsConsent', e.target.checked)}
                      className="mt-1 mr-3 h-5 w-5 text-orange-600 focus:ring-orange-500 border-2 border-blue-400 rounded"
                    />
                    <label htmlFor="smsConsent" className="cursor-pointer flex-grow">
                      <div className="flex items-center mb-2">
                        <span className="mr-2 text-lg">📱</span>
                        <span className="font-bold text-gray-900 text-base">SMS Text Message Updates</span>
                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Optional</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>I agree to receive SMS text messages about my service request and contractor updates.</p>
                      </div>
                    </label>
                  </div>
                  <div className="mt-3 p-2 bg-white rounded border border-blue-200">
                    <p className="text-xs text-gray-500">
                      Message frequency varies. Message and data rates may apply.
                      Reply <span className="font-mono font-bold">STOP</span> to unsubscribe.
                      Reply <span className="font-mono font-bold">HELP</span> for assistance.
                      View <a href="/sms-disclosure" target="_blank" className="text-orange-600 hover:underline">SMS Terms</a>.
                    </p>
                  </div>
                  <p className="mt-2 text-xs text-blue-800">
                    <strong>SMS is not required.</strong> Without SMS, you'll receive updates via email only.
                  </p>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={!formData.contactInfo.name || !formData.contactInfo.phone || !formData.privacyConsent || isSubmitting}
                  className="w-full p-3 bg-orange-500 text-white font-bold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-600 transition-colors"
                >
                  {isSubmitting ? (
                    <>
                      <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                      Finding Matches...
                    </>
                  ) : (
                    'Find My Matches'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Navigation */}
          {currentStep > 1 && (
            <div className="mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={prevStep}
                className="px-4 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors"
              >
                ← Previous
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SafeSmartMatchingWizard;